import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset password. The link may have expired.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#0055FF] flex items-center justify-center"><span className="text-white font-black text-lg font-[Chivo]">A</span></div>
          <div>
            <h1 className="font-[Chivo] font-bold text-zinc-950 text-lg tracking-tight">AMCAL NEWMARKET</h1>
            <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Compounding Pharmacy</p>
          </div>
        </div>

        {!token ? (
          <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-2" data-testid="reset-no-token">
            <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
            <span>This reset link is missing its token. Please use the link from your email, or request a new one.</span>
          </div>
        ) : done ? (
          <div className="space-y-5" data-testid="reset-success">
            <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-start gap-2">
              <CheckCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
              <span>Your password has been reset. You can now sign in with your new password.</span>
            </div>
            <button onClick={() => navigate("/login")} className="w-full bg-[#0055FF] text-white font-semibold rounded-none px-6 py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98]" data-testid="go-to-login-button">
              GO TO SIGN IN
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            <div>
              <p className="font-[Chivo] text-2xl font-black tracking-tight text-zinc-950">Set a new password</p>
              <p className="text-sm text-zinc-500 mt-1">Choose a strong password you don't use elsewhere.</p>
            </div>
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reset-error">{error}</div>}
            <div>
              <Label htmlFor="new-password" className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">New Password</Label>
              <Input id="new-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="mt-1.5 rounded-none" data-testid="new-password-input" />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Confirm Password</Label>
              <Input id="confirm-password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={6} className="mt-1.5 rounded-none" data-testid="confirm-password-input" />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-[#0055FF] text-white font-semibold rounded-none px-6 py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98] disabled:opacity-50" data-testid="reset-submit-button">
              {loading ? "RESETTING..." : "RESET PASSWORD"}
            </button>
            <Link to="/login" className="block text-center text-sm text-zinc-500 hover:text-zinc-950 transition-colors" data-testid="reset-back-to-login">Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
}
